import i18n from "../i18n";
import {
  Text,
  Menu,
  Badge,
  Stack,
  Group,
  Button,
  Grid,
  ActionIcon,
  UnstyledButton,
  ThemeIcon,
  Modal,
} from "@mantine/core";
import {
  IconAdjustments,
  IconCircleCheck,
  IconCopy,
  IconPlus,
  IconSettings,
  IconTrash,
} from "@tabler/icons";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import _ from "lodash";
import { useAuthUser } from "react-auth-kit";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useServerApi } from "../hooks/userServerApi";
import { useTabNavigate } from "../hooks/useTabNavigate";
import { CommissionScheduleProductCard } from "./commissionScheduleProductList";
import ProductCommissionScheduleEditor from "./productCommissionScheduleEditor";
import RemoteSelect2 from "./remoteSelect2";
import { scheduleHasCoverRate } from "../helpers/commissionScheduleYears";

const VersionViewer = ({
  rateFactor = 1,
  scheduleProductId,
  version,
  name,
  showGrossRateOnly = false,
}) => {
  return (
    <div>
      <CommissionScheduleProductCard
        scheduleProductId={scheduleProductId}
        version={version}
        showCode={false}
        showProductInfo={false}
        name={name}
        showGrossRateOnly={showGrossRateOnly}
        useCoverAsGross={showGrossRateOnly}
        rateFactor={rateFactor}
      />
    </div>
  );
};

const ProductCommissionScheduleTable = ({
  form,
  name,
  disabled,
  rateFactor = 1, //0.525
  firstYearMaxPercentage, // for first year rate limit
  version = "COMPANY", //"CONSULTANT"
  showGrossRateOnly = false,
  ...props
}) => {
  const { t: translate } = useTranslation();
  const [api] = useServerApi();
  const location = useLocation();
  const tabNav = useTabNavigate();
  const id = form.values._id;
  const [scheduleProductList, setScheduleProductList] = useState([]);
  const [selected, setSelected] = useState(0);
  const auth = useAuthUser();

  const isSelected = (index) => index === selected;
  const isCurrentTable = (scheduleProduct) => {
    if (!scheduleProduct?.effectiveDate) return false;
    const now = new Date();
    const effective = new Date(scheduleProduct.effectiveDate);
    const end = scheduleProduct.endDate ? new Date(scheduleProduct.endDate) : null;
    if (Number.isNaN(effective.getTime())) return false;
    if (now < effective) return false;
    if (end && !Number.isNaN(end.getTime()) && now > end) return false;
    return true;
  };
  const canEdit = version === "COMPANY" && !disabled && !!id;
  const [editor, setEditor] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [copyTargetOpen, setCopyTargetOpen] = useState(false);
  const [copyTarget, setCopyTarget] = useState(null);
  const [copying, setCopying] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const closeCopyTarget = () => {
    setCopyTargetOpen(false);
    setCopyTarget(null);
  };

  const copyToProduct = async () => {
    if (!copyTarget?._id || !selectedScheduleProduct?._id || copying) return;
    setCopying(true);
    try {
      const result = await api.Product.copyCommissionScheduleProduct(copyTarget._id, {
        sourceScheduleProductId: selectedScheduleProduct._id,
      });
      const code = result?.scheduleProduct?.code || "";
      toast.success(
        `${i18n.t("Commission Copied")}${code ? ` ${code}` : ""}`.trim()
      );
      closeCopyTarget();
      const targetId = String(copyTarget._id);
      window.dispatchEvent(
        new CustomEvent("mbid:open-form-tab", {
          detail: { tabsKey: "t1", recordId: targetId, value: "Commission" },
        })
      );
      window.dispatchEvent(
        new CustomEvent("mbid:commission-schedule-changed", {
          detail: { productId: targetId },
        })
      );
      tabNav.open({
        pathname: location.pathname,
        search: `?id=${targetId}&mode=edit&formTab=Commission`,
        label: copyTarget.name || "Product",
      });
    } catch (error) {
      toast.error(error?.message || "Unable to copy product commission");
    } finally {
      setCopying(false);
    }
  };

  const VersionSelector = () => (
    <>
      {scheduleProductList.map((scheduleProduct, index) => (
        <UnstyledButton
          key={index}
          style={{ width: "100%" }}
          onClick={() => {
            setSelected(index);
          }}
        >
          <div
            key={index}
            style={{
              border: "1px solid",
              borderColor: isSelected(index) ? "lightgray" : "gray",
              padding: "10px",
              marginBottom: "20px",
              borderRadius: 10,
            }}
          >
            <Group position="apart">
              <Group spacing={6}>
                <Text size="sm" weight={800}>
                  {"V" + (scheduleProductList.length - index)}
                </Text>
                <Badge
                  size="xs"
                  radius={0}
                  color={scheduleProduct.professionalInvestor ? "orange" : "gray"}
                >
                  {scheduleProduct.professionalInvestor ? "PI" : "Non-PI"}
                </Badge>
                {version !== "CONSULTANT" &&
                  scheduleHasCoverRate(scheduleProduct.commissionTable) && (
                    <Badge size="xs" radius={0} color="blue">
                      $C
                    </Badge>
                  )}
              </Group>

              {isCurrentTable(scheduleProduct) && (
                <ThemeIcon
                  color="teal"
                  size={20}
                  radius="xl"
                  aria-label="Current commission version"
                >
                  <IconCircleCheck size={18} />
                </ThemeIcon>
              )}
            </Group>

            {/* <Group position="apart"> */}
            <Text color="dimmed" size="xs">
              {translate("Start : ")}
              {scheduleProduct.effectiveDate
                ? moment(scheduleProduct.effectiveDate).format("YYYY-MM-DD")
                : " ---"}
            </Text>

            <Text color="dimmed" size="xs">
              {translate("End : ")}
              {scheduleProduct.endDate
                ? moment(scheduleProduct.endDate).format("YYYY-MM-DD")
                : " ---"}
            </Text>
            {scheduleProduct.commissionSchedule?.code && (
              <Text color="dimmed" size={10} mt={4}>
                {scheduleProduct.commissionSchedule.code} &gt; {scheduleProduct.code}
              </Text>
            )}
          </div>
        </UnstyledButton>
      ))}
    </>
  );

  const fetchScheduleProductList = async () => {
    try {
      if (!id) return;
      const result = await api.Product.getCommissionScheduleProductList(id);
      //   console.log(result);
      setScheduleProductList(result);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchScheduleProductListRef = useRef(fetchScheduleProductList);
  fetchScheduleProductListRef.current = fetchScheduleProductList;

  useEffect(() => {
    fetchScheduleProductList();
  }, [id]);

  useEffect(() => {
    if (!id) return undefined;
    const handler = (event) => {
      if (String(event.detail?.productId || "") !== String(id)) return;
      setSelected(0);
      setRefreshToken((token) => token + 1);
      fetchScheduleProductListRef.current();
    };
    window.addEventListener("mbid:commission-schedule-changed", handler);
    return () => window.removeEventListener("mbid:commission-schedule-changed", handler);
  }, [id]);

  const [actualRateFactor, setActualRateFactor] = useState(0);

  const setActualRateByConsultant = async () => {
    if (!auth().id) return;

    //get producter rate by consultant
    const result = await api.User.getProducerRate(auth().id);
    if (!result || !result.rate) {
      setActualRateFactor(0);
      return;
    }
    setActualRateFactor(result.rate);
  };

  useEffect(() => {
    if (version === "COMPANY") {
      setActualRateFactor(1);
    } else {
      setActualRateByConsultant();
    }
  }, [version, auth().id]);

  const selectedScheduleProduct = scheduleProductList[selected];

  const versionName = selectedScheduleProduct
    ? "V" + (scheduleProductList.length - selected)
    : "";

  const closeDelete = () => {
    if (deleting) return;
    setDeleteOpen(false);
  };

  const deleteSelected = async () => {
    if (!id || !selectedScheduleProduct?._id || deleting) return;
    setDeleting(true);
    try {
      await api.Product.deleteCommissionScheduleProduct(id, {
        scheduleProductId: selectedScheduleProduct._id,
      });
      toast.success(i18n.t("Commission version deleted"));
      setDeleteOpen(false);
      window.dispatchEvent(
        new CustomEvent("mbid:commission-schedule-changed", {
          detail: { productId: String(id) },
        })
      );
    } catch (error) {
      toast.error(error?.message || "Unable to delete product commission");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      {_.isEmpty(scheduleProductList) && (
        <Stack spacing="sm">
          <Text size="sm" color="dimmed">
            No Commission Schedule Data
          </Text>
          {canEdit && (
            <div>
              <Button
                leftIcon={<IconPlus size={16} />}
                onClick={() => setEditor({ mode: "create", scheduleProductId: null })}
              >
                Add Commission
              </Button>
            </div>
          )}
        </Stack>
      )}
      {!_.isEmpty(scheduleProductList) && (
        <Stack spacing="sm">
          {canEdit && (
            <Group>
              <Menu shadow="md" width={200}>
                <Menu.Target>
                  <ActionIcon variant="default" aria-label="Commission actions">
                    <IconSettings size={20} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>{i18n.t("Action")}</Menu.Label>
                  <Menu.Item
                    icon={<IconAdjustments size={14} />}
                    onClick={() =>
                      setEditor({
                        mode: "edit",
                        scheduleProductId: selectedScheduleProduct?._id,
                      })
                    }
                  >{i18n.t("Edit")}</Menu.Item>
                  <Menu.Item
                    icon={<IconCopy size={14} />}
                    onClick={() =>
                      setEditor({
                        mode: "create",
                        scheduleProductId: selectedScheduleProduct?._id,
                      })
                    }
                  >
                    {i18n.t("Copy to New")}
                  </Menu.Item>
                  <Menu.Item
                    icon={<IconCopy size={14} />}
                    onClick={() => {
                      setCopyTarget(null);
                      setCopyTargetOpen(true);
                    }}
                  >
                    {i18n.t("Copy to Product")}
                  </Menu.Item>
                  <Menu.Item
                    color="red"
                    icon={<IconTrash size={14} />}
                    onClick={() => setDeleteOpen(true)}
                  >
                    {i18n.t("Delete Version")}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          )}
          <Grid grow gutter="xl">
            <Grid.Col span={10}>
              <VersionViewer
                key={`${selectedScheduleProduct?._id || "none"}-${refreshToken}`}
                rateFactor={actualRateFactor}
                scheduleProductId={selectedScheduleProduct?._id}
                version={version}
                name={versionName}
                showGrossRateOnly={showGrossRateOnly}
              />
            </Grid.Col>

            <Grid.Col span={2}>
              <VersionSelector scheduleProductList={scheduleProductList} />
            </Grid.Col>
          </Grid>
        </Stack>
      )}
      {deleteOpen && (
        <Modal
          opened
          onClose={closeDelete}
          title={i18n.t("Delete Version")}
          transitionDuration={0}
        >
          <Stack>
            {versionName && (
              <Text size="sm" weight={600}>
                {versionName}
                {selectedScheduleProduct?.code ? ` · ${selectedScheduleProduct.code}` : ""}
              </Text>
            )}
            <Text size="sm">
              {i18n.t("Delete this commission version? This cannot be undone.")}
            </Text>
            <Group position="right">
              <Button variant="default" onClick={closeDelete} disabled={deleting}>
                {i18n.t("Cancel")}
              </Button>
              <Button color="red" onClick={deleteSelected} loading={deleting}>
                {i18n.t("Delete")}
              </Button>
            </Group>
          </Stack>
        </Modal>
      )}
      <Modal
        opened={copyTargetOpen}
        onClose={closeCopyTarget}
        title={i18n.t("Copy to Product")}
        transitionDuration={0}
      >
        <Stack>
          <RemoteSelect2
            apiEntity="product"
            value={copyTarget?._id || null}
            valueField="_id"
            labelField="name"
            labelRender={(data) =>
              `[ ${data.code} ] ${data.name}${data.cname ? ` ${data.cname}` : ""}`
            }
            searchFields={["name", "code", "cname"]}
            sort={{ by: "code", order: "asc" }}
            placeholder={translate("Search Product By Name/ Code/ Chinese name")}
            preQuery={id ? { _id: { $ne: id } } : undefined}
            onDataChange={setCopyTarget}
          />
          <Group position="right">
            <Button variant="default" onClick={closeCopyTarget} disabled={copying}>
              {i18n.t("Cancel")}
            </Button>
            <Button onClick={copyToProduct} loading={copying} disabled={!copyTarget?._id}>
              {i18n.t("Copy")}
            </Button>
          </Group>
        </Stack>
      </Modal>
      {editor && canEdit && (
        <ProductCommissionScheduleEditor
          key={`${editor.mode}-${editor.scheduleProductId || "new"}`}
          mode={editor.mode}
          productId={id}
          scheduleProductId={editor.scheduleProductId}
          versionName={editor.mode === "edit" ? versionName : ""}
          onClose={() => setEditor(null)}
          onSaved={(savedMode) => {
            setEditor(null);
            setRefreshToken((token) => token + 1);
            if (savedMode === "create") setSelected(0);
            fetchScheduleProductList();
          }}
        />
      )}
    </div>
  );
};

export default ProductCommissionScheduleTable;
